/* Sanitising and sizing for generated vector art.
 *
 * The art in `generated_art.svg` is written by a language model and then served
 * back to browsers, so it is treated as untrusted markup: an allowlist parser
 * runs on the way IN (before the row is written) and again on the way OUT (in
 * the /api/art route). An element outside the allowlist rejects the whole
 * document rather than being stripped — a rejected drawing just means the
 * author regenerates, which is cheap — while an attribute outside the
 * allowlist is simply dropped.
 *
 * The output is served as image/svg+xml, so browsers parse it with an XML
 * parser: duplicate attributes and undeclared namespace prefixes are FATAL
 * there, not merely untidy. The rebuild below therefore emits each attribute
 * name at most once and declares exactly one namespace.
 *
 * Plain module (no "use server") so client components can import the limits.
 */

/** Generated art is line work, not photography — a few KB is plenty. */
export const SVG_MAX_BYTES = 60_000;

/** Elements a plate or cover motif may use. Deliberately no <script>,
 * <foreignObject>, <animate*>, <image>, or filter primitives (feImage can
 * fetch remote content). */
const ALLOWED_TAGS = new Set([
  "svg",
  "g",
  "defs",
  "title",
  "desc",
  "symbol",
  "use",
  "path",
  "rect",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "text",
  "tspan",
  "linearGradient",
  "radialGradient",
  "stop",
  "clipPath",
  "mask",
  "pattern",
]);

/** Geometry + presentation attributes. No `style` (it can smuggle url()), no
 * event handlers, no xlink:* beyond href. */
const ALLOWED_ATTRS = new Set([
  "id",
  "class",
  "transform",
  "viewBox",
  "preserveAspectRatio",
  "d",
  "points",
  "x",
  "y",
  "x1",
  "y1",
  "x2",
  "y2",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "fr",
  "fx",
  "fy",
  "dx",
  "dy",
  "width",
  "height",
  "offset",
  "fill",
  "fill-opacity",
  "fill-rule",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-miterlimit",
  "opacity",
  "clip-path",
  "clip-rule",
  "mask",
  "stop-color",
  "stop-opacity",
  "gradientUnits",
  "gradientTransform",
  "spreadMethod",
  "patternUnits",
  "patternContentUnits",
  "patternTransform",
  "clipPathUnits",
  "maskUnits",
  "maskContentUnits",
  "font-family",
  "font-size",
  "font-weight",
  "font-style",
  "letter-spacing",
  "text-anchor",
  "dominant-baseline",
  "paint-order",
  "vector-effect",
  "shape-rendering",
  // Plain SVG2 href only. `xlink:href` would need an xmlns:xlink declaration to
  // parse as XML at all, so it is dropped rather than carried.
  "href",
]);

const SVG_NS = "http://www.w3.org/2000/svg";

/** One tag, attributes still unparsed. Quoted attribute values may contain `>`. */
const TAG = /<\/?([A-Za-z][A-Za-z0-9:-]*)((?:[^>"']|"[^"]*"|'[^']*')*?)\/?>/g;
const ATTR = /([A-Za-z_:][A-Za-z0-9_.:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;

/**
 * Rebuild `input` keeping only allowlisted elements and attributes.
 * Returns null when the document isn't a usable, safe SVG.
 */
export function sanitizeSvg(input: string): string | null {
  if (!input) return null;

  // Strip the prose a model sometimes wraps markup in, plus comments,
  // processing instructions and doctypes (which can carry entities).
  let svg = input.trim();
  const fence = svg.match(/```(?:svg|xml|html)?\s*([\s\S]*?)```/);
  if (fence) svg = fence[1].trim();
  const open = svg.indexOf("<svg");
  const close = svg.lastIndexOf("</svg>");
  if (open === -1 || close === -1 || close < open) return null;
  svg = svg.slice(open, close + "</svg>".length);
  svg = svg.replace(/<!--[\s\S]*?-->/g, "").replace(/<\?[\s\S]*?\?>/g, "");
  if (/<!/.test(svg)) return null; // doctype / CDATA / entity declaration
  if (Buffer.byteLength(svg, "utf8") > SVG_MAX_BYTES) return null;

  let out = "";
  let cursor = 0;
  let depth = 0;
  let sawRoot = false;
  let hasViewBox = false;
  TAG.lastIndex = 0;

  for (let m = TAG.exec(svg); m; m = TAG.exec(svg)) {
    const [raw, tagName, attrText] = m;
    if (!ALLOWED_TAGS.has(tagName)) return null;

    const isClose = raw.startsWith("</");
    const isSelfClosing = /\/>$/.test(raw);
    if (isClose) {
      depth -= 1;
      if (depth < 0) return null;
    } else if (tagName === "svg") {
      if (sawRoot || depth !== 0) return null; // exactly one root <svg>
      sawRoot = true;
    }

    // Text between tags (titles, <text> content) carries through escaped.
    out += escapeText(svg.slice(cursor, m.index));
    cursor = m.index + raw.length;

    if (isClose) {
      out += `</${tagName}>`;
      continue;
    }

    const attrs: string[] = [];
    const seen = new Set<string>(); // duplicate attribute names are fatal in XML
    ATTR.lastIndex = 0;
    for (let a = ATTR.exec(attrText); a; a = ATTR.exec(attrText)) {
      const name = a[1];
      const value = (a[2] ?? a[3] ?? "").trim();
      if (!ALLOWED_ATTRS.has(name) || seen.has(name)) continue;
      if (name === "href" && !value.startsWith("#")) continue;
      // Nothing may reference anything off-document.
      if (/url\(\s*(?!#)/i.test(value) || /javascript:|data:|&#/i.test(value)) continue;
      if (name === "viewBox") hasViewBox = true;
      seen.add(name);
      attrs.push(`${name}="${escapeAttr(value)}"`);
    }

    // The one namespace declaration, added here rather than carried through, so
    // it is present exactly once and always correct.
    if (tagName === "svg") attrs.unshift(`xmlns="${SVG_NS}"`);
    out += `<${tagName}${attrs.length ? " " + attrs.join(" ") : ""}${isSelfClosing ? "/>" : ">"}`;
    if (!isSelfClosing) depth += 1;
  }

  out += escapeText(svg.slice(cursor));
  if (!sawRoot || depth !== 0 || !hasViewBox) return null;
  return out;
}

function escapeText(text: string) {
  // Any stray markup characters in text nodes become entities.
  return text.replace(/&(?!(?:[a-zA-Z]+|#\d+|#x[0-9a-fA-F]+);)/g, "&amp;").replace(/</g, "&lt;");
}

function escapeAttr(value: string) {
  return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

/** URL the browser loads generated art from (see src/app/api/art/[kind]/[id]). */
export function artUrl(kind: "cover" | "plate", id: string, version?: string | number) {
  const suffix = version ? `?v=${encodeURIComponent(String(version))}` : "";
  return `/api/art/${kind}/${id}${suffix}`;
}
